import React, { createContext, useContext, useEffect, useState } from 'react';
import { clearSupabaseAuthStorage, supabase } from '../lib/supabase';
import type { User, Session } from '@supabase/supabase-js';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signUp: (email: string, password: string) => Promise<{ error: Error | null }>;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const getSessionId = () => {
    if (typeof window === 'undefined') return '';
    return (
      window.localStorage.getItem('cms_session_id') ||
      window.localStorage.getItem('session_id') ||
      process.env.PROJECT_ID ||
      ''
    ).trim();
  };

  const signUp = async (email: string, password: string) => {
    try {
      const sessionId = getSessionId();
      const res = await fetch('/api/auth/register', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          ...(sessionId ? { 'X-Session-Id': sessionId } : {}),
        },
        body: JSON.stringify({ email, password }),
      });
      const result = await res.json();
      if (!result.success) {
        return { error: new Error(result.error?.message || 'Registration failed') };
      }

      // Cookie 已由服务端写入，同时更新 Supabase 客户端 session 保持 SDK 兼容
      const authSession = result?.data?.session;
      if (authSession?.access_token && authSession?.refresh_token) {
        await supabase.auth.setSession({
          access_token: authSession.access_token,
          refresh_token: authSession.refresh_token,
        });
      }

      return { error: null };
    } catch (error) {
      return { error: error as Error };
    }
  };

  const signIn = async (email: string, password: string) => {
    try {
      const sessionId = getSessionId();
      const res = await fetch('/api/auth/login', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          ...(sessionId ? { 'X-Session-Id': sessionId } : {}),
        },
        body: JSON.stringify({ email, password }),
      });
      const result = await res.json();
      if (!result.success) {
        return { error: new Error(result.error?.message || 'Login failed') };
      }

      // Cookie 已由服务端写入，同时更新 Supabase 客户端 session 保持 SDK 兼容
      const authSession = result?.data?.session;
      if (authSession?.access_token && authSession?.refresh_token) {
        const { error } = await supabase.auth.setSession({
          access_token: authSession.access_token,
          refresh_token: authSession.refresh_token,
        });
        return { error };
      }

      return { error: null };
    } catch (error) {
      return { error: error as Error };
    }
  };

  const signOut = async () => {
    // 先通知服务端清 Cookie；失败也不应阻止前端立即退出
    await fetch('/api/auth/logout', {
      method: 'POST',
      credentials: 'include',
    }).catch(() => {});

    // 只做本地退出，避免网络问题导致“点了没反应”
    await (supabase.auth.signOut as any)({ scope: 'local' }).catch(() => {});
    clearSupabaseAuthStorage();
    setSession(null);
    setUser(null);
  };

  const value = {
    user,
    session,
    loading,
    signUp,
    signIn,
    signOut,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
